/* eslint-disable react/no-unescaped-entities */
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, AlertTriangle, Mail } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';

const TermsPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-xl flex items-center gap-2">
                Terms of Service
                <Badge variant="outline" className="text-amber-500 border-amber-500">BETA</Badge>
              </h1>
              <p className="text-sm text-slate-400">Last updated: January 15, 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-3xl font-bold mb-6">SlideCon Terms of Service</h1>
          
          {/* Beta Notice */}
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 my-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-amber-400 mb-2">BETA SOFTWARE NOTICE</h3>
                <p className="text-slate-300">
                  SlideCon is currently in <strong>BETA</strong> and is provided on an "AS IS" basis. 
                  Features may change, be modified, or removed without prior notice. By using this 
                  beta software, you acknowledge and accept the inherent risks, including but not 
                  limited to potential data loss, service interruptions, and unexpected behavior.
                </p>
              </div>
            </div>
          </div>
          
          <p className="text-slate-300 mb-6">
            Welcome to SlideCon. These Terms of Service ("Terms") govern your access to and use of 
            SlideCon's services, including our website, applications, and AI-powered presentation 
            tools (collectively, the "Service"). <strong>SlideCon is a DBA (Doing Business As) of 
            RDK Services LLC.</strong> By accessing or using the Service, you agree to be bound by 
            these Terms.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
          <p className="text-slate-300">
            By creating an account or using SlideCon, you agree to these Terms of Service, our 
            Privacy Policy, and our Acceptable Use Policy. If you are using the Service on behalf 
            of an organization, you represent that you have the authority to bind that organization 
            to these Terms. <strong>Your continued use of the Service constitutes acceptance of 
            these Terms and any future modifications.</strong>
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">2. Description of Service</h2>
          <p className="text-slate-300">
            SlideCon provides AI-powered tools for creating presentations and slideshows, including:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Document-to-slideshow conversion</li>
            <li>AI-generated presentation content</li>
            <li>Worship and educational presentation tools</li>
            <li>Community features for churches and educational institutions</li>
            <li>Live streaming and presentation broadcasting</li>
            <li>Direct messaging between community members</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">3. User Accounts</h2>
          <p className="text-slate-300">
            To use certain features of the Service, you must create an account. You agree to:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Promptly notify us of any unauthorized access to your account</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">4. Prohibited Content and Conduct</h2>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 my-4">
            <h3 className="text-lg font-semibold text-red-400 mb-2">STRICTLY PROHIBITED</h3>
            <p className="text-slate-300 mb-3">
              SlideCon maintains a strict policy against inappropriate content. The following 
              is absolutely prohibited on our platform:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li><strong>Sexual Content:</strong> Any sexually explicit or suggestive material, including but not limited to pornography, erotic content, or sexual imagery</li>
              <li><strong>Nudity:</strong> Any images or content depicting nudity, partial nudity, or revealing/provocative content</li>
              <li><strong>Adult Material:</strong> Any content intended for adult audiences only or that would be inappropriate for minors</li>
              <li><strong>Exploitation:</strong> Any content that exploits, harms, or endangers minors in any way</li>
            </ul>
          </div>
          <p className="text-slate-300">
            Additionally, you may not use the Service to:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights of others</li>
            <li>Harass, threaten, or intimidate other users</li>
            <li>Distribute malware, spam, or phishing content</li>
            <li>Attempt to gain unauthorized access to the Service</li>
            <li>Use the Service for any illegal purpose</li>
            <li>Share content promoting violence, hate speech, or discrimination</li>
            <li>Impersonate any person or entity</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">5. Content Moderation and Enforcement</h2>
          <p className="text-slate-300">
            We reserve the right to review, monitor, and remove any content that violates these 
            Terms. Violations of our prohibited content policies may result in:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Immediate removal of offending content</li>
            <li>Suspension or termination of your account</li>
            <li>Reporting to appropriate law enforcement authorities</li>
            <li>Permanent ban from the platform</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">6. Intellectual Property</h2>
          <p className="text-slate-300">
            The Service and its original content, features, and functionality are owned by 
            RDK Services LLC (operating as SlideCon) and are protected by international copyright, 
            trademark, and other intellectual property laws. You retain ownership of content you 
            create using our Service, but grant us a license to host and display your content as 
            necessary to provide the Service.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">7. AI-Generated Content</h2>
          <p className="text-slate-300">
            Our Service uses artificial intelligence to generate and enhance content. You 
            acknowledge that:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>AI-generated content may not always be accurate or appropriate</li>
            <li>You are responsible for reviewing AI-generated content before use</li>
            <li>We do not guarantee the accuracy or suitability of AI outputs</li>
            <li>AI features are provided "as is" without warranties</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">8. Disclaimer of Warranties</h2>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 my-4">
            <p className="text-slate-300 uppercase text-sm">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. 
              RDK SERVICES LLC DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, 
              OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICE IS FREE OF 
              VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
          </div>
          <p className="text-slate-300">
            DURING THE BETA PERIOD, YOU SPECIFICALLY ACKNOWLEDGE THAT:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>The software may contain bugs, errors, and defects</li>
            <li>Features may be incomplete, change, or be removed</li>
            <li>Data loss may occur without warning</li>
            <li>Service interruptions may occur at any time</li>
            <li>No Service Level Agreements (SLAs) apply during beta</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">9. Limitation of Liability</h2>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 my-4">
            <p className="text-slate-300 uppercase text-sm">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL RDK SERVICES LLC, 
              ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, OR LICENSORS BE LIABLE FOR 
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, 
              INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR 
              OTHER INTANGIBLE LOSSES (EVEN IF RDK SERVICES LLC HAS BEEN ADVISED OF THE POSSIBILITY 
              OF SUCH DAMAGES), ARISING OUT OF OR IN CONNECTION WITH:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-1 mt-3 text-sm">
              <li>YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE</li>
              <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE</li>
              <li>ANY CONTENT OBTAINED FROM THE SERVICE</li>
              <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT</li>
              <li>DATA LOSS OR CORRUPTION</li>
              <li>SERVICE INTERRUPTIONS OR DOWNTIME</li>
            </ul>
          </div>
          <p className="text-slate-300">
            IN NO EVENT SHALL RDK SERVICES LLC'S TOTAL LIABILITY TO YOU FOR ALL DAMAGES EXCEED 
            THE AMOUNT YOU PAID, IF ANY, FOR ACCESSING THE SERVICE DURING THE TWELVE (12) MONTHS 
            PRIOR TO THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">10. Indemnification</h2>
          <p className="text-slate-300">
            You agree to defend, indemnify, and hold harmless RDK Services LLC, its officers, 
            directors, employees, agents, and affiliates from and against any claims, damages, 
            obligations, losses, liabilities, costs, and expenses (including attorney's fees) 
            arising from:
          </p>
          <ul className="list-disc pl-6 text-slate-300 space-y-2">
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Any content you submit to the Service</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">11. Assumption of Risk</h2>
          <p className="text-slate-300">
            YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. 
            YOU ASSUME FULL RESPONSIBILITY FOR ANY LOSS OR DAMAGE ARISING FROM YOUR USE OF THE 
            SERVICE, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF BUSINESS OPPORTUNITIES, 
            OR ANY OTHER COMMERCIAL DAMAGES OR LOSSES.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">12. Termination</h2>
          <p className="text-slate-300">
            We may terminate or suspend your account and access to the Service immediately, 
            without prior notice, for conduct that we believe violates these Terms or is 
            harmful to other users, us, or third parties, or for any other reason at our 
            sole discretion. Upon termination, your right to use the Service will cease immediately.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">13. Governing Law and Dispute Resolution</h2>
          <p className="text-slate-300">
            These Terms shall be governed by and construed in accordance with the laws of the 
            State of Florida, United States, without regard to its conflict of law provisions.
          </p>
          <p className="text-slate-300 mt-3">
            <strong>Dispute Resolution:</strong> Any dispute arising from these Terms or your use 
            of the Service shall first be attempted to be resolved through good-faith negotiation. 
            If negotiation fails, disputes shall be resolved through binding arbitration in 
            accordance with the rules of the American Arbitration Association. Any arbitration 
            shall take place in Florida, United States.
          </p>
          <p className="text-slate-300 mt-3">
            <strong>Class Action Waiver:</strong> YOU AGREE THAT ANY CLAIMS WILL BE BROUGHT IN 
            YOUR INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED 
            CLASS OR REPRESENTATIVE PROCEEDING.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">14. Severability</h2>
          <p className="text-slate-300">
            If any provision of these Terms is held to be invalid, illegal, or unenforceable, 
            the remaining provisions shall continue in full force and effect. The invalid, 
            illegal, or unenforceable provision shall be modified to the minimum extent necessary 
            to make it valid, legal, and enforceable while preserving its intent.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">15. Entire Agreement</h2>
          <p className="text-slate-300">
            These Terms, together with the Privacy Policy and Acceptable Use Policy, constitute 
            the entire agreement between you and RDK Services LLC regarding the Service and 
            supersede all prior agreements, understandings, and communications, whether written 
            or oral.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">16. Changes to Terms</h2>
          <p className="text-slate-300">
            We reserve the right to modify these Terms at any time. We will notify you of 
            significant changes by posting a notice on our Service or sending you an email. 
            Your continued use of the Service after changes constitute acceptance of the new Terms.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">17. Contact Information</h2>
          <p className="text-slate-300">
            For questions about these Terms of Service, please contact us at:
          </p>
          <div className="bg-slate-800 rounded-lg p-4 mt-4">
            <p className="text-slate-300">
              <strong>SlideCon (a DBA of RDK Services LLC)</strong><br />
              <br />
              <strong>Customer Service:</strong><br />
              <a href="mailto:cs@myrdkllc.com" className="text-blue-400 hover:underline flex items-center gap-2">
                <Mail className="w-4 h-4" />
                cs@myrdkllc.com
              </a>
              <br /><br />
              <strong>Mailing Address:</strong><br />
              15810 Gale Ave. Suite 285<br />
              Hacienda Heights, CA 91745<br />
              United States
            </p>
          </div>

          <div className="mt-12 pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              By using SlideCon, you acknowledge that you have read, understood, and agree 
              to be bound by these Terms of Service. You further acknowledge that SlideCon 
              is currently in BETA and you accept all risks associated with using beta software.
            </p>
            <p className="text-slate-500 text-xs mt-4">
              © {new Date().getFullYear()} RDK Services LLC. All rights reserved. SlideCon is a 
              trademark of RDK Services LLC.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
